import { useAuth } from '@app/core/auth/authentication/hooks/useAuth';
import { KnockFeedProvider, KnockProvider } from '@knocklabs/react';
import { NotificationsNotificationsSessionDto } from '@mi-front/api-v2';
import { useTheme } from '@mui/material';
import { ReactNode, useCallback } from 'react';
import { useNotificationsSessionContext } from './useNotificationsSessionContext';

export type KnockNotificationsProviderProps = {
  session: NotificationsNotificationsSessionDto;
  feedChannelId: string;
  children: ReactNode;
};

/**
 * Authenticates the Knock React SDK and connects the in-app feed channel.
 */
export function KnockNotificationsProvider({
  session,
  feedChannelId,
  children,
}: KnockNotificationsProviderProps) {
  const theme = useTheme();
  const { client } = useAuth();
  const { refetchSession } = useNotificationsSessionContext();

  const handleUserTokenExpiring = useCallback(async () => {
    const next = await refetchSession();
    return next?.token;
  }, [refetchSession]);

  return (
    <KnockProvider
      apiKey={session.publicApiKey}
      user={{ id: session.userId }}
      userToken={session.token}
      onUserTokenExpiring={handleUserTokenExpiring}
      enabled
    >
      <KnockFeedProvider
        feedId={feedChannelId}
        colorMode={theme.palette.mode === 'dark' ? 'dark' : 'light'}
        defaultFeedOptions={client?.id ? { tenant: client.id } : undefined}
      >
        {children}
      </KnockFeedProvider>
    </KnockProvider>
  );
}
